import type { IntentConfig, ResolvedGuardrails } from "@fathom/intent";
import type { ProjectionContext } from "../types.js";

/**
 * Generate a .cursorrules file from intent and guardrails.
 *
 * Cursor rules are plain text/markdown — no frontmatter.
 * More concise than the Claude skill, focused on actionable rules.
 */
export function generateCursorRules(
  intent: IntentConfig,
  guardrails: ResolvedGuardrails,
  context?: ProjectionContext,
): string {
  const sections: string[] = [];

  // Project overview
  sections.push(`# ${intent.project}`);
  sections.push("");
  sections.push(intent.description);
  if (intent.audience) {
    sections.push(`Audience: ${intent.audience}`);
  }

  // Values as rules
  if (intent.values.length > 0) {
    sections.push("");
    sections.push("## Values");
    const priorityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
    const sorted = [...intent.values].sort(
      (a, b) => priorityOrder[a.priority] - priorityOrder[b.priority],
    );
    for (const value of sorted) {
      const priority =
        value.priority === "critical" || value.priority === "high"
          ? ` [${value.priority}]`
          : "";
      sections.push(`- ${value.name}${priority}: ${value.description}`);
    }
  }

  // Budget
  if (intent.budget) {
    sections.push("");
    sections.push("## Budget");
    sections.push(
      `- Monthly limit: ${intent.budget.currency} ${intent.budget.monthly_limit.toLocaleString()}`,
    );
    if (intent.budget.prefer_batch) {
      sections.push("- Prefer batch operations to reduce costs");
    }
    if (context?.trackingSummary) {
      const pct = Math.round(
        (context.trackingSummary.monthlySpend / intent.budget.monthly_limit) *
          100,
      );
      sections.push(
        `- Current monthly spend: ${intent.budget.currency} ${context.trackingSummary.monthlySpend.toFixed(2)} (${pct}%)`,
      );
    }
  }

  // Guardrails — all as flat rules
  const allRules = [
    ...guardrails.security.map((r) => `[security] ${r}`),
    ...guardrails.quality.map((r) => `[quality] ${r}`),
    ...guardrails.process.map((r) => `[process] ${r}`),
  ];

  if (allRules.length > 0) {
    sections.push("");
    sections.push("## Rules");
    for (const rule of allRules) {
      sections.push(`- ${rule}`);
    }
  }

  // Tech
  if (intent.tech) {
    const { stack, opinions } = intent.tech;
    if (stack.length > 0 || opinions.length > 0) {
      sections.push("");
      sections.push("## Tech Stack");
      if (stack.length > 0) {
        sections.push(`Stack: ${stack.join(", ")}`);
      }
      if (opinions.length > 0) {
        for (const opinion of opinions) {
          sections.push(`- ${opinion}`);
        }
      }
    }
  }

  // Registry
  if (context?.registry && context.registry.length > 0) {
    sections.push("");
    sections.push("## Features");
    for (const feature of context.registry) {
      const tokens = feature.estimatedTokens
        ? ` (~${Math.round(feature.estimatedTokens / 1000)}K tokens)`
        : "";
      sections.push(`- ${feature.name} [${feature.status}]${tokens}`);
    }
  }

  return sections.join("\n") + "\n";
}
